import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import type { EntitySpec, FieldDef } from '../../models/Models';
import { api } from '../../services/Server';
import { formatApiError } from '../../utils/Error';
import { displayName, normalizeBoolean, renderDateTime } from './helpers';
import { useFkOptions } from './hooks';
import type { AnyEntity } from './types';

interface EditorProps {
  spec: EntitySpec;
  initial: Partial<AnyEntity> | null;
  onClose: () => void;
  onSaved: () => void;
  groupFilter?: string; // when set, only fields belonging to this group are shown
}

const FkSelect = ({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  const { data, isLoading } = useFkOptions(field.fkPath);
  return (
    <TextField
      select
      label={field.label}
      required={field.required}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      fullWidth
      helperText={isLoading ? 'Loading…' : undefined}
    >
      <MenuItem value="">-</MenuItem>
      {data?.map((opt) => (
        <MenuItem key={opt.id} value={opt.id}>
          {displayName(opt)}
        </MenuItem>
      ))}
    </TextField>
  );
};

const groupFields = (fields: FieldDef[]): { group: string; fields: FieldDef[] }[] => {
  const order: string[] = [];
  const map = new Map<string, FieldDef[]>();
  for (const f of fields) {
    const g = f.group ?? 'Details';
    if (!map.has(g)) { order.push(g); map.set(g, []); }
    map.get(g)!.push(f);
  }
  return order.map((g) => ({ group: g, fields: map.get(g)! }));
};

const coerce = (f: FieldDef, v: unknown): unknown => {
  if (f.type === 'number' || f.type === 'fk') {
    return v === '' || v === null || v === undefined ? null : Number(v);
  }
  if (f.type === 'boolean') {
    // Stored as Text in the legacy schema - send as 'true'/'false' strings
    // (matches existing data and what `renderBoolean` already accepts).
    const norm = normalizeBoolean(v);
    return norm === '' ? null : norm;
  }
  return v === '' || v === undefined ? null : v;
};

/** Shared image uploader used for both logos (small square) and pictures (larger). */
const ImageInput = ({
  field,
  value,
  onChange,
  maxBytes,
  width,
  height,
  emptyText,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  maxBytes: number;
  width: number;
  height: number;
  emptyText: string;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dataUri = typeof value === 'string' && value.startsWith('data:image') ? value : null;
  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`Image too large (max ${Math.round(maxBytes / 1024)} KB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.onerror = () => toast.error('Failed to read image.');
    reader.readAsDataURL(file);
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
        {field.label}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box
          sx={{
            width,
            height,
            borderRadius: 2,
            border: '1px dashed #C7CFD8',
            bgcolor: '#FAFBFD',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {dataUri ? (
            <Box
              component="img"
              src={dataUri}
              alt={`${field.label} preview`}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: '#8E99A8' }}>
              {emptyText}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={() => inputRef.current?.click()}>
            {dataUri ? 'Replace' : 'Upload'}
          </Button>
          {dataUri && (
            <Button variant="text" size="small" color="error" onClick={() => onChange(null)}>
              Remove
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Stack>
    </Box>
  );
};

const FieldInput = ({
  field: f,
  value: v,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  if (f.readOnly) {
    const display =
      f.type === 'datetime'
        ? renderDateTime(v)
        : v === null || v === undefined || v === ''
        ? '-'
        : String(v);
    return (
      <TextField
        label={f.label}
        value={display}
        fullWidth
        disabled
        helperText="Set automatically by the server"
      />
    );
  }
  if (f.type === 'fk') {
    return <FkSelect field={f} value={v} onChange={onChange} />;
  }
  if (f.type === 'logo') {
    return <ImageInput field={f} value={v} onChange={onChange} maxBytes={2 * 1024 * 1024} width={96} height={96} emptyText="No logo" />;
  }
  if (f.type === 'picture') {
    return <ImageInput field={f} value={v} onChange={onChange} maxBytes={5 * 1024 * 1024} width={200} height={150} emptyText="No picture" />;
  }
  if (f.type === 'boolean') {
    const norm = normalizeBoolean(v);
    return (
      <TextField
        select
        label={f.label}
        value={norm}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
        fullWidth
      >
        <MenuItem value="">-</MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </TextField>
    );
  }
  if (f.type === 'enum') {
    return (
      <TextField
        select
        label={f.label}
        required={f.required}
        value={(v as string | null | undefined) ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        fullWidth
      >
        <MenuItem value="">-</MenuItem>
        {(f.options ?? []).map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      label={f.label}
      required={f.required}
      multiline={f.type === 'multiline'}
      minRows={f.type === 'multiline' ? 2 : undefined}
      type={f.type === 'number' ? 'number' : 'text'}
      value={v ?? ''}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
    />
  );
};

const Editor = ({ spec, initial, onClose, onSaved, groupFilter }: EditorProps) => {
  const isCreate = !initial?.id;
  const [values, setValues] = useState<Record<string, unknown>>(initial ?? {});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [tabIndex, setTabIndex] = useState(0);

  // When a groupFilter is active, only show the fields belonging to that group
  // ``document`` / ``documents`` are managed inline in the Profile view via
  // the ``/documents`` API \u2014 they are not entity-row columns and have no
  // place in the create/update payload.
  const visibleFields = (groupFilter
    ? spec.fields.filter((f) => (f.group ?? 'Details') === groupFilter)
    : spec.fields
  ).filter((f) => f.type !== 'document' && f.type !== 'documents');

  const groups = useMemo(() => groupFields(visibleFields), [visibleFields]);

  useEffect(() => {
    setValues(initial ?? {});
    setDirty(new Set());
    setTabIndex(0);
  }, [initial]);

  const setField = (name: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      // Create: send every field the user touched (or that has a default).
      // Update: PATCH semantics - only send fields the user actually changed.
      // Server-managed (readOnly) fields are never sent.
      const includeName = (n: string) =>
        isCreate ? values[n] !== undefined : dirty.has(n);
      const numCoerce = (v: unknown) =>
        v === '' || v === null || v === undefined ? null : Number(v);
      const txtCoerce = (v: unknown) =>
        v === '' || v === undefined ? null : v;
      for (const f of visibleFields) {
        if (f.readOnly) continue;
        if (f.type === 'range' && f.range) {
          // Range fields fan out into (from, to[, comment]) payload keys;
          // the synthetic ``f.name`` is never sent.
          if (includeName(f.range.from)) payload[f.range.from] = numCoerce(values[f.range.from]);
          if (includeName(f.range.to)) payload[f.range.to] = numCoerce(values[f.range.to]);
          if (f.range.comment && includeName(f.range.comment)) {
            payload[f.range.comment] = txtCoerce(values[f.range.comment]);
          }
          continue;
        }
        if (includeName(f.name)) payload[f.name] = coerce(f, values[f.name]);
      }
      return isCreate ? api.create(spec.path, payload) : api.update(spec.path, initial!.id!, payload);
    },
    onSuccess: () => {
      toast.success(`${spec.singular} ${isCreate ? 'created' : 'updated'}`);
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  // Render a ``from - to`` numeric pair plus an optional comment, all on one
  // row. Used by ``type: 'range'`` fields.
  const renderRange = (f: FieldDef) => {
    const r = f.range!;
    return (
      <Stack key={f.name} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
          <TextField
            label={`${f.label} - from`}
            type="number"
            value={values[r.from] ?? ''}
            onChange={(e) => setField(r.from, e.target.value)}
            fullWidth
          />
          <Typography sx={{ color: 'text.secondary', px: 0.5 }}>-</Typography>
          <TextField
            label="to"
            type="number"
            value={values[r.to] ?? ''}
            onChange={(e) => setField(r.to, e.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
        {r.comment && (
          <Box sx={{ flex: 1 }}>
            <TextField
              label="Comment"
              multiline
              minRows={1}
              value={values[r.comment] ?? ''}
              onChange={(e) => setField(r.comment!, e.target.value)}
              fullWidth
            />
          </Box>
        )}
      </Stack>
    );
  };

  // Render a list of fields, collapsing ``inlineWithNext`` pairs into one row
  // and ``range`` fields into a (from - to) + comment row.
  const renderFields = (fields: FieldDef[]) => {
    const out: ReactNode[] = [];
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (f.type === 'range' && f.range) {
        out.push(renderRange(f));
        continue;
      }
      const next = fields[i + 1];
      if (f.inlineWithNext && next) {
        out.push(
          <Stack key={f.name} direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <FieldInput field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldInput field={next} value={values[next.name]} onChange={(v) => setField(next.name, v)} />
            </Box>
          </Stack>,
        );
        i += 1;
        continue;
      }
      out.push(
        <FieldInput key={f.name} field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />,
      );
    }
    return out;
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { height: '85vh' } } }}>
      <DialogTitle>
        {isCreate
          ? `New ${spec.singular}`
          : groupFilter
          ? `Edit ${spec.singular} - ${groupFilter}`
          : `Edit ${spec.singular} #${initial?.id}`}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* ── Tabbed layout (create / full edit with multiple groups) ── */}
        {!groupFilter && groups.length > 1 ? (
          <>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: '#FAFBFD',
                minHeight: 44,
                flexShrink: 0,
              }}
            >
              {groups.map(({ group }) => (
                <Tab
                  key={group}
                  label={group}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', minHeight: 44 }}
                />
              ))}
            </Tabs>
            <Box sx={{ overflowY: 'auto', flex: 1, p: 2.5 }}>
              <Stack spacing={2}>
                {renderFields(groups[tabIndex]?.fields ?? [])}
              </Stack>
            </Box>
          </>
        ) : (
          /* ── Flat layout (single-group edit) ── */
          <Box sx={{ overflowY: 'auto', flex: 1, p: 2.5 }}>
            <Stack spacing={2}>
              {renderFields(visibleFields)}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="transparent-text-blue">
          Cancel
        </Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Editor;
